import {
    COLLECT_ENABLED,
    DEFAULT_COLLECT_URL,
    DEFAULT_HEALTH_FLUSH_DELAY_MAX_MS,
    DEFAULT_HEALTH_FLUSH_DELAY_MIN_MS,
    DEFAULT_HEALTH_FLUSH_THRESHOLD,
    DEFAULT_HEALTH_URL,
    DEFAULT_FLUSH_THRESHOLD,
    MAX_EVENTS_PER_REQUEST,
    MAX_HEALTH_CHECKS_PER_REQUEST,
    VISITOR_ID_STORAGE_KEY,
} from './constants';
import {normalizeHealthChecks, sendMetricHealth} from './health';
import {createMetricCallEvent} from './metric-call-event';
import {getCookie, setCookie} from 'methods/utils';

function hasDebugCookie() {
    return typeof document !== 'undefined' && document.cookie.indexOf('debug=1') !== -1;
}

function debugLog(...args) {
    if (hasDebugCookie() && typeof console !== 'undefined' && typeof console.log === 'function') {
        console.log('[MetrikaCollector]', ...args);
    }
}

function debugGroup(title, payload, callback) {
    if (!hasDebugCookie() || typeof console === 'undefined') {
        return callback();
    }

    const canGroup = typeof console.groupCollapsed === 'function' && typeof console.groupEnd === 'function';

    if (!canGroup) {
        debugLog(title, payload);
        return callback();
    }

    console.groupCollapsed('[MetrikaCollector]', title, payload);

    try {
        return callback();
    } finally {
        console.groupEnd();
    }
}

function createVisitorId() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }

    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
        const random = Math.random() * 16 | 0;
        const value = char === 'x' ? random : (random & 0x3 | 0x8);

        return value.toString(16);
    });
}

/**
 * Transport: queues metric_call events and POSTs them to metrika /collect/ (flush after each track).
 */
export default class MetrikaCollector {
    constructor(options = {}) {
        this.endpoint = options.endpoint || DEFAULT_COLLECT_URL;
        this.healthEndpoint = options.healthEndpoint || DEFAULT_HEALTH_URL;
        this.debug = Boolean(options.debug);
        this.flushThreshold = options.flushThreshold ?? DEFAULT_FLUSH_THRESHOLD;
        this.healthFlushThreshold = options.healthFlushThreshold ?? DEFAULT_HEALTH_FLUSH_THRESHOLD;
        this.healthFlushDelayMinMs = options.healthFlushDelayMinMs ?? DEFAULT_HEALTH_FLUSH_DELAY_MIN_MS;
        this.healthFlushDelayMaxMs = options.healthFlushDelayMaxMs ?? DEFAULT_HEALTH_FLUSH_DELAY_MAX_MS;
        this.visitorIdStorageKey = options.visitorIdStorageKey || VISITOR_ID_STORAGE_KEY;
        this.fixedVisitorId = options.visitorId ?? null;
        this.getPageUrl = options.getPageUrl || (() => window.location.href);
        this.queue = [];
        this.flushPromise = null;
        this.flushScheduled = false; // хрень для сжатия событий, если разом сгенерилось 2-3 события они отправятся в одном вызове
        this.healthQueue = [];
        this.healthFlushPromise = null;
        this.healthFlushTimer = null;
        this.healthSessionCookieKey = options.healthSessionStorageKey || 'metrika_health_sent';
        this.healthFlushAtMs = Date.now() + this._getHealthFlushDelayMs();
        this.destroyed = false;

        this._onPageHide = () => {
            this.flush({useBeacon: true});
            this.flushHealth({useBeacon: true});
        };

        if (options.bindPageLifecycle !== false && typeof window !== 'undefined') {
            window.addEventListener('pagehide', this._onPageHide);
        }
    }

    getVisitorId() {
        if (this.fixedVisitorId) {
            return this.fixedVisitorId;
        }

        if (typeof sessionStorage !== 'undefined') {
            try {
                let id = sessionStorage.getItem(this.visitorIdStorageKey);

                if (!id) {
                    id = getCookie(this.visitorIdStorageKey);

                    if (!id) {
                        id = createVisitorId();
                    }

                    sessionStorage.setItem(this.visitorIdStorageKey, id);
                    setCookie(this.visitorIdStorageKey, id);
                }

                return id;
            } catch (e) {
                // Fallback below.
            }
        }

        const cookieId = getCookie(this.visitorIdStorageKey);
        if (cookieId) {
            this._sessionVisitorId = cookieId;
            return cookieId;
        }

        if (!this._sessionVisitorId) {
            this._sessionVisitorId = createVisitorId();
            setCookie(this.visitorIdStorageKey, this._sessionVisitorId);
        }

        return this._sessionVisitorId;
    }

    track(input) {
        if (!COLLECT_ENABLED) {
            return 0;
        }

        const items = Array.isArray(input) ? input : [input];
        let accepted = 0;

        debugGroup('track', {received: items.length}, () => {
            for (const item of items) {
                const event = createMetricCallEvent(item, this.getPageUrl);

                if (!event) {
                    if (typeof console !== 'undefined' && console.warn) {
                        console.warn('[MetrikaCollector] skipped invalid metric_call', item);
                    }
                    continue;
                }

                this.queue.push(event);
                accepted += 1;
                debugLog('queued metric_call', {
                    provider: event.provider,
                    action: event.action,
                    status: event.status,
                    queueSize: this.queue.length,
                });
            }
        });

        if (accepted > 0) {
            this._scheduleFlush();
        }

        return accepted;
    }

    _scheduleFlush() {
        if (this.flushScheduled || this.destroyed) {
            return;
        }

        this.flushScheduled = true;

        const run = () => {
            this.flushScheduled = false;
            this.flush();
        };

        if (typeof queueMicrotask === 'function') {
            queueMicrotask(run);
        } else {
            setTimeout(run, 0);
        }
    }

    flush(options = {}) {
        if (this.destroyed || this.queue.length === 0) {
            return Promise.resolve(true);
        }

        if (this.flushPromise) {
            return this.flushPromise;
        }

        debugLog('flush start', {
            batchSize: Math.min(this.queue.length, MAX_EVENTS_PER_REQUEST),
            useBeacon: Boolean(options.useBeacon),
        });
        this.flushPromise = this._sendBatch(options).finally(() => {
            this.flushPromise = null;

            if (this.queue.length > 0) {
                this._scheduleFlush();
            }
        });

        return this.flushPromise;
    }

    destroy() {
        this.destroyed = true;

        if (this.healthFlushTimer !== null) {
            clearTimeout(this.healthFlushTimer);
            this.healthFlushTimer = null;
        }

        if (typeof window !== 'undefined') {
            window.removeEventListener('pagehide', this._onPageHide);
        }
    }

    sendHealth(checks, options = {}) {
        return sendMetricHealth(this, checks, options);
    }

    collectHealth(checks, options = {}) {
        if (!COLLECT_ENABLED) {
            return 0;
        }

        const items = normalizeHealthChecks(checks);

        if (items.length === 0 || this.destroyed) {
            debugLog('skip health collection', {
                reason: this.destroyed ? 'destroyed' : 'empty',
            });
            return 0;
        }

        if (this._isHealthSentForSession()) {
            debugLog('skip health collection', {
                reason: 'already_sent_for_session',
            });
            return 0;
        }

        const availableSlots = MAX_HEALTH_CHECKS_PER_REQUEST - this.healthQueue.length;

        if (availableSlots <= 0) {
            debugLog('skip health collection', {
                reason: 'queue_full',
            });
            return 0;
        }

        const queuedCount = Math.min(items.length, availableSlots);

        debugGroup('collectHealth', {received: items.length, queued: queuedCount}, () => {
            this.healthQueue.push(...items.slice(0, availableSlots));
            debugLog('queued health checks', {
                received: items.length,
                queued: queuedCount,
                queueSize: this.healthQueue.length,
            });

            if (options.immediate === true) {
                this.flushHealth(options);
                return;
            }

            if (this.healthQueue.length >= this.healthFlushThreshold) {
                this.flushHealth(options);
            } else {
                this._scheduleHealthFlush();
            }
        });

        return queuedCount;
    }

    flushHealth(options = {}) {
        if (this.destroyed || this.healthQueue.length === 0 || this._isHealthSentForSession()) {
            return Promise.resolve(true);
        }

        if (this.healthFlushTimer !== null) {
            clearTimeout(this.healthFlushTimer);
            this.healthFlushTimer = null;
        }

        if (this.healthFlushPromise) {
            return this.healthFlushPromise;
        }

        debugGroup('health flush', {
            batchSize: Math.min(this.healthQueue.length, MAX_HEALTH_CHECKS_PER_REQUEST),
            useBeacon: Boolean(options.useBeacon),
        }, () => {
            debugLog('health flush start', {
                batchSize: Math.min(this.healthQueue.length, MAX_HEALTH_CHECKS_PER_REQUEST),
                useBeacon: Boolean(options.useBeacon),
            });
        });
        this.healthFlushPromise = this._sendHealthBatch(options).then((sent) => {
            if (sent) {
                this._markHealthSentForSession();
                this.healthQueue = [];

                return true;
            }

            this.healthFlushAtMs = Date.now() + this._getHealthFlushDelayMs();
            this._scheduleHealthFlush();

            return false;
        }).finally(() => {
            this.healthFlushPromise = null;
        });

        return this.healthFlushPromise;
    }

    _takeBatch() {
        return this.queue.splice(0, MAX_EVENTS_PER_REQUEST);
    }

    _scheduleHealthFlush() {
        if (this.destroyed || this.healthFlushTimer !== null || this.healthQueue.length === 0 || this._isHealthSentForSession()) {
            return;
        }

        const delay = Math.max(0, this.healthFlushAtMs - Date.now());
        debugLog('health flush scheduled', {
            delay,
            queueSize: this.healthQueue.length,
        });

        this.healthFlushTimer = setTimeout(() => {
            this.healthFlushTimer = null;
            this.flushHealth();
        }, delay);
    }

    _takeHealthBatch() {
        return this.healthQueue.splice(0, MAX_HEALTH_CHECKS_PER_REQUEST);
    }

    _buildBody(events) {
        const payload = {events};
        const visitorId = this.getVisitorId();

        if (visitorId) {
            payload.visitor_id = visitorId;
        }

        return JSON.stringify(payload);
    }

    async _sendBatch({useBeacon = false} = {}) {
        const events = this._takeBatch();

        if (events.length === 0) {
            return true;
        }

        const body = this._buildBody(events);

        if (useBeacon && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
            navigator.sendBeacon(this.endpoint, new Blob([body], {type: 'application/json'}));

            return true;
        }

        const headers = {'Content-Type': 'application/json'};

        if (this.debug) {
            headers['X-Metrika-Debug'] = '1';
        }

        try {
            const response = await fetch(this.endpoint, {
                method: 'POST',
                headers,
                body,
                keepalive: true,
            });

            return response.status === 204 || response.status === 200;
        } catch (e) {
            if (typeof console !== 'undefined' && console.warn) {
                console.warn('[MetrikaCollector] flush failed', e);
            }

            this.queue.unshift(...events);

            return false;
        }
    }

    async _sendHealthBatch({useBeacon = false} = {}) {
        const checks = this._takeHealthBatch();

        if (checks.length === 0) {
            return true;
        }

        try {
            const sent = await sendMetricHealth(this, checks, {useBeacon});

            if (!sent) {
                this.healthQueue.unshift(...checks);
            }

            return sent;
        } catch (error) {
            this.healthQueue.unshift(...checks);

            return false;
        }
    }

    _isHealthSentForSession() {
        return getCookie(this.healthSessionCookieKey) === '1';
    }

    _markHealthSentForSession() {
        this._healthSentForSession = true;
        setCookie(this.healthSessionCookieKey, '1');
    }

    _getHealthFlushDelayMs() {
        const minDelay = Math.max(0, Number(this.healthFlushDelayMinMs) || 0);
        const maxDelay = Math.max(minDelay, Number(this.healthFlushDelayMaxMs) || minDelay);

        return Math.floor(minDelay + (Math.random() * (maxDelay - minDelay)));
    }
}
